import React, { useMemo } from 'react';
import { BlockMath, InlineMath } from 'react-katex';

import type { MathEquationPayload } from '../../core/types/payloads/math_equation.types';
import { normalizeEquationLatex, splitEquationWithBlanks } from './math_equation.logic';

function EquationBlankBox() {
  return (
    <span
      className="math-equation-blank inline-flex h-9 min-w-[2.75rem] items-center justify-center rounded-md border border-slate-300 bg-white px-2 align-middle shadow-sm"
      aria-label="Espacio para completar"
    />
  );
}

function EquationWithBlanks({ equationLatex }: { equationLatex: string }) {
  const segments = useMemo(() => splitEquationWithBlanks(equationLatex), [equationLatex]);
  const hasBlanks = segments.some((segment) => segment.type === 'blank');

  if (!hasBlanks) {
    return (
      <div className="math-equation__katex text-2xl md:text-3xl">
        <BlockMath math={normalizeEquationLatex(equationLatex)} errorColor="#64748b" />
      </div>
    );
  }

  return (
    <div className="math-equation__inline flex flex-wrap items-center justify-center gap-2 text-2xl md:text-3xl">
      {segments.map((segment, index) =>
        segment.type === 'blank' ? (
          <EquationBlankBox key={`blank-${index}`} />
        ) : segment.value.trim() ? (
          <span key={`math-${index}`} className="math-equation__katex">
            <InlineMath math={segment.value} errorColor="#64748b" />
          </span>
        ) : null,
      )}
    </div>
  );
}

export interface MathEquationViewProps {
  payload: MathEquationPayload;
  /** Muestra pasos de resolución (solucionario docente). */
  showResolutionSteps?: boolean;
}

export function MathEquationView({ payload, showResolutionSteps = true }: MathEquationViewProps) {
  const instruction = String(payload.instruction || '').trim();
  const steps = Array.isArray(payload.resolutionSteps)
    ? payload.resolutionSteps.map((step) => String(step || '').trim()).filter(Boolean)
    : [];

  return (
    <article className="math-equation rounded-xl border border-slate-100 bg-white/80 p-6 print:border-[#ddd] print:bg-white">
      {instruction ? (
        <p className="math-equation__instruction mb-4 text-sm font-medium text-slate-600 print:text-slate-700">
          {instruction}
        </p>
      ) : null}

      <div className="math-equation__stage my-6 flex items-center justify-center rounded-xl border border-slate-100 bg-slate-50/50 p-8 print:border-[#e2e8f0] print:bg-[#f8fafc]">
        <EquationWithBlanks equationLatex={payload.equationLatex} />
      </div>

      {showResolutionSteps && steps.length > 0 ? (
        <aside className="math-equation__solution mt-4 rounded-lg border border-dashed border-slate-200 bg-slate-50/40 px-4 py-3 print:border-[#cbd5e1]">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Pasos de resolución (docente)
          </p>
          <ol className="list-decimal space-y-1 pl-5 text-sm leading-relaxed text-slate-600">
            {steps.map((step, index) => (
              <li key={`step-${index}`}>{step}</li>
            ))}
          </ol>
        </aside>
      ) : null}
    </article>
  );
}
